import { useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import parsePhoneNumberFromString from "libphonenumber-js";
import { cn } from "../../lib/cn";

export interface Recipient {
  raw: string;
  e164: string | null;
}

interface RecipientInputProps {
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
}

/** Same libphonenumber check the API runs, so an invalid chip is caught before submit. */
function toRecipient(raw: string): Recipient {
  const trimmed = raw.trim();
  const parsed = parsePhoneNumberFromString(trimmed);
  return { raw: trimmed, e164: parsed?.isValid() ? parsed.number : null };
}

/** Splits on commas, semicolons, whitespace and newlines so a pasted column just works. */
function splitMany(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function RecipientInput({ value, onChange }: RecipientInputProps) {
  const [draft, setDraft] = useState("");

  function commit(text: string) {
    const additions = splitMany(text)
      .map(toRecipient)
      // Re-adding a number already in the list would send it twice; the API dedupes too, but
      // silently dropping it there would leave the UI claiming a recipient count that's wrong.
      .filter((candidate) => !value.some((existing) => existing.raw === candidate.raw));
    if (additions.length > 0) onChange([...value, ...additions]);
    setDraft("");
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === "," || event.key === ";") {
      event.preventDefault();
      if (draft.trim()) commit(draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!/[\s,;]/.test(text)) return; // single number — let the normal input handle it
    event.preventDefault();
    commit(text);
  }

  const invalidCount = value.filter((r) => !r.e164).length;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-text" htmlFor="recipient-input">
        Recipients
      </label>

      <div
        className={cn(
          "flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-control border border-border",
          "bg-surface px-2 py-1.5 transition-colors duration-fast ease-standard",
          "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20",
        )}
      >
        {value.map((recipient, index) => (
          <span
            key={`${recipient.raw}-${index}`}
            className={cn(
              "inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-medium",
              recipient.e164
                ? "bg-accent-soft text-accent"
                : "bg-danger-soft text-danger line-through",
            )}
            title={recipient.e164 ?? "Not a valid phone number"}
          >
            {recipient.e164 ?? recipient.raw}
            <button
              type="button"
              aria-label={`Remove ${recipient.raw}`}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
              className={cn(
                "rounded-pill transition-opacity hover:opacity-70",
                "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
              )}
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}

        <input
          id="recipient-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => draft.trim() && commit(draft)}
          placeholder={value.length === 0 ? "+923001234567, +14155550123…" : ""}
          className="min-w-[12rem] flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
        />
      </div>

      <p className="text-xs text-text-muted">
        {value.length === 0
          ? "Type or paste numbers in international format. Enter, comma or a pasted column adds them."
          : `${value.length - invalidCount} valid${invalidCount > 0 ? `, ${invalidCount} invalid` : ""}`}
      </p>
    </div>
  );
}
